import { t } from "@lingui/core/macro";
import { NavigationProp, useNavigation } from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { useAtomValue, useSetAtom } from "jotai";
import { useMemo } from "react";
import { Platform } from "react-native";
import { useFragment } from "react-relay";

import {
  useIsClubSellingMemberships,
  useIsClubSellingStandardPackages,
} from "@/features/Club";
import { navigationRef_atom } from "@/features/Navigation";
import { useGoToStore } from "@/features/Navigation/hooks/useGoToStore";
import { useLessonTabHandler } from "@/features/Navigation/hooks/useLessonTabHandler";
import { useNavigationLabels } from "@/features/Navigation/hooks/useNavigationLabels";
import { getEventNavigationLabel } from "@/features/Navigation/utils/eventNavigationLabel";
import { showClubSwitcherModalAtom } from "@/features/Session";
import { useCurrentClubHasVisibleLessons } from "@/features/Session/hooks/useCurrentClubHasVisibleLessons";
import { HomeTabsNavigatorParamList } from "@/navigators/HomeTabsNavigator/types";
import { useFlags } from "@/utils/launchDarkly";
import {
  HomeIcon,
  LessonsIcon,
  EventsIcon,
  ClubSwitcherIcon,
  MembersIcon,
  RedirectIcon,
  PersonOutlineIcon,
  StoreIcon,
} from "@whoosh/design-system/src/components/icons_v2/general";
import { IconProps } from "@whoosh/design-system/src/components/primitive";
import { getNounOverride } from "@whoosh/utilities/src/FormatNounOverride/getNounOverride";

import { useGetSideNavBarItemsFragment_session$key } from "./__generated__/useGetSideNavBarItemsFragment_session.graphql";

export type TabBarItem = {
  routeHandler: () => void;
  label: string;
  icon: (props: IconProps) => React.JSX.Element;
  routeName?: string;
  selected: boolean;
  isDivider?: boolean;
};

const LessonRouteNames = ["Lessons", "LessonsHome", "InstructorDetail"];
const ProfileRouteNames = ["ProfileHome", "ProfilePlayHistory", "Profile"];
const HomeRouteNames = ["Home", "MainHome"];
const EventsRouteNames = ["Events", "EventDetail"];

export const useGetSideNavBarItems = ({
  openMemberModal,
  sessionRef,
}: {
  openMemberModal: () => void;
  sessionRef: useGetSideNavBarItemsFragment_session$key | null;
}) => {
  const navigationRef = useAtomValue(navigationRef_atom);
  const setShowClubSwitcherModal = useSetAtom(showClubSwitcherModalAtom);

  const navigation =
    useNavigation<NavigationProp<HomeTabsNavigatorParamList>>();

  const routeName = navigationRef?.isReady()
    ? navigationRef?.getCurrentRoute()?.name
    : null;

  const session =
    useFragment(useGetSideNavBarItemsFragmentString, sessionRef) || null;

  const goToStore = useGoToStore(session);

  const lessonTabHandler = useLessonTabHandler(session);
  const club = session?.currentClientProfile?.member?.club;

  const hasClubThatIsNotCurrent =
    session?.user?.activeMemberProfiles?.edges &&
    !session.user.activeMemberProfiles.edges.find(
      (e) => e?.node?.member?.club.slug === club?.slug
    );

  const canSwitchContext =
    (session?.user?.activeMemberProfiles?.count || 0) > 1 ||
    hasClubThatIsNotCurrent ||
    (Platform.OS === "web" && session?.staffAccessAvailable);

  const hasEvents = !!club?.departments?.edges?.some(
    (e) => e?.node?.anyFacilityEventsEnabled
  );
  const hasMemberLessonBooking = useCurrentClubHasVisibleLessons(session);

  const isAnonymousUser = !session?.currentClientProfile?.id;

  const { memAppRosterVisibility } = useFlags();

  const externalLinks = club?.appCustomization?.externalLinks;

  const showMemberRoster = memAppRosterVisibility && club?.supportsPrivate;
  const lessonLabel = getNounOverride(
    club?.copyOverrides?.lesson?.plural,
    t`Lessons`
  );
  const eventLabel = getEventNavigationLabel(
    club?.copyOverrides?.event?.plural
  );
  const navigationLabels = useNavigationLabels();
  const externalLinksNavItems: TabBarItem[] = [];

  const isClubSellingMemberships = useIsClubSellingMemberships(club);
  const isClubSellingStandardPackages = useIsClubSellingStandardPackages(club);

  const shouldShowStore =
    isClubSellingMemberships || isClubSellingStandardPackages;

  const items = useMemo(() => {
    const itemArray: TabBarItem[] = [
      {
        routeName: "MainHome",
        routeHandler: () => {
          navigation.navigate("MainHome");
        },
        label: navigationLabels.home,
        icon: HomeIcon,
        selected: routeName ? HomeRouteNames.includes(routeName) : false,
      },
    ];

    if (hasMemberLessonBooking)
      itemArray.push({
        routeName: "Lessons",
        routeHandler: lessonTabHandler,
        label: lessonLabel,
        icon: LessonsIcon,
        selected: routeName ? LessonRouteNames.includes(routeName) : false,
      });

    if (hasEvents)
      itemArray.push({
        routeName: "Events",
        routeHandler: () => {
          navigation.navigate("Events");
        },
        label: eventLabel,
        icon: EventsIcon,
        selected: routeName ? EventsRouteNames.includes(routeName) : false,
      });

    if (showMemberRoster)
      itemArray.push({
        routeName: "Members",
        routeHandler: () => {
          openMemberModal();
        },
        label: navigationLabels.members,
        icon: MembersIcon,
        selected: false,
      });

    if (shouldShowStore) {
      itemArray.push({
        routeHandler: () => {
          goToStore();
        },
        label: navigationLabels.store,
        icon: StoreIcon,
        selected: false,
      });
    }

    if (!isAnonymousUser)
      itemArray.push({
        routeName: "Profile",
        routeHandler: () => {
          navigation.navigate("Profile", { screen: "ProfileHome" });
        },
        label: navigationLabels.profile,
        icon: PersonOutlineIcon,
        selected: routeName ? ProfileRouteNames.includes(routeName) : false,
      });

    if (canSwitchContext) {
      itemArray.push({
        routeHandler: () => {
          setShowClubSwitcherModal(true);
        },
        label: navigationLabels.switchClub,
        icon: ClubSwitcherIcon,
        selected: false,
      });
    }

    return itemArray;
  }, [
    routeName,
    hasMemberLessonBooking,
    lessonTabHandler,
    lessonLabel,
    hasEvents,
    eventLabel,
    navigationLabels.home,
    navigationLabels.members,
    navigationLabels.profile,
    navigationLabels.store,
    navigationLabels.switchClub,
    showMemberRoster,
    shouldShowStore,
    isAnonymousUser,
    canSwitchContext,
    navigation,
    openMemberModal,
    goToStore,
    setShowClubSwitcherModal,
  ]);

  externalLinks?.forEach((link) => {
    if (link?.url && link?.displayName) {
      externalLinksNavItems.push({
        routeHandler: () => {
          if (Platform.OS === "web") {
            window.open(link.url, "_blank");
          } else {
            navigation.navigate("WebView", {
              url: link.url,
              title: link.displayName,
            });
          }
        },
        label: link.displayName,
        icon: ({ color }) => <RedirectIcon color={color} />,
        selected: false,
      });
    }
  });

  return { items, links: externalLinksNavItems };
};

export const useGetSideNavBarItemsFragmentString = graphql`
  fragment useGetSideNavBarItemsFragment_session on Session {
    staffAccessAvailable
    ...useGoToStore_session
    ...useCurrentClubHasVisibleLessons_session
    ...useLessonTabHandler_session
    user {
      ... on MemberUser {
        activeMemberProfiles: memberProfiles(active: true, hidden: false) {
          count
          edges {
            node {
              hidden
              member {
                club {
                  slug
                }
              }
            }
          }
        }
      }
    }
    currentClientProfile {
      ... on MemberProfile {
        id
      }
      member {
        club {
          ...useIsClubSellingStandardPackages_club
          ...useIsClubSellingMemberships_club
          supportsPublic
          supportsPrivate
          slug
          copyOverrides {
            event {
              plural
            }
            lesson {
              plural
            }
          }
          appCustomization {
            externalLinks {
              displayName
              url
            }
          }
          departments {
            edges {
              node {
                anyFacilityEventsEnabled
                anyFacilityLessonsEnabled
              }
            }
          }
        }
      }
    }
  }
`;
